import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquareText, Send, Plus, Users, UserCircle, Search, ArrowLeft, LogOut, X, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Button, Field, Input, Select, Modal, Badge, Textarea } from "@/components/ui-kit";

/**
 * Iter v34j (#1) — Karyawan-to-Karyawan chat page (/karyawan/chat-teman).
 * Supports DM (1-on-1) and Group threads.
 * Auto-poll every 5 seconds for new messages.
 */
export default function KaryawanChat() {
  const { user } = useAuth();
  const me = (user?.email || "").toLowerCase();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newKind, setNewKind] = useState("dm");
  const [directory, setDirectory] = useState([]);
  const [peerEmail, setPeerEmail] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupMembers, setGroupMembers] = useState([]); // emails
  const [search, setSearch] = useState("");
  const messagesEndRef = useRef(null);

  const loadThreads = async () => {
    try { const r = await api.get("/karyawan-chat/threads"); setThreads(r.data); }
    catch { setThreads([]); }
  };
  const loadDirectory = async () => {
    try { const r = await api.get("/karyawan-chat/directory"); setDirectory(r.data); }
    catch { setDirectory([]); }
  };
  const loadMessages = async (tid) => {
    if (!tid) return;
    try {
      const r = await api.get(`/karyawan-chat/threads/${tid}/messages`);
      setMessages(r.data);
      // Mark as read
      api.post(`/karyawan-chat/threads/${tid}/read`).then(loadThreads).catch(() => {});
    } catch {/* ignore */}
  };

  useEffect(() => { loadThreads(); loadDirectory(); }, []);
  useEffect(() => { loadMessages(activeId); }, [activeId]);
  useEffect(() => {
    const id = setInterval(() => {
      loadThreads();
      if (activeId) loadMessages(activeId);
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const activeThread = threads.find((t) => t.id === activeId);
  const filteredThreads = search
    ? threads.filter((t) => (t.display_name || "").toLowerCase().includes(search.toLowerCase()))
    : threads;

  const send = async () => {
    if (!input.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post(`/karyawan-chat/threads/${activeId}/messages`, { body: input.trim() });
      setInput("");
      loadMessages(activeId);
      loadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal kirim"); }
    finally { setSending(false); }
  };

  const createThread = async () => {
    try {
      if (newKind === "dm") {
        if (!peerEmail) return toast.error("Pilih karyawan");
        const r = await api.post("/karyawan-chat/threads", { kind: "dm", peer_email: peerEmail });
        setActiveId(r.data.id);
      } else {
        if (!groupName.trim()) return toast.error("Nama grup wajib");
        if (groupMembers.length < 1) return toast.error("Pilih minimal 1 anggota lain");
        const r = await api.post("/karyawan-chat/threads", { kind: "group", name: groupName.trim(), member_emails: groupMembers });
        setActiveId(r.data.id);
      }
      setNewOpen(false);
      setPeerEmail(""); setGroupName(""); setGroupMembers([]);
      loadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal buat thread"); }
  };

  const leaveOrDelete = async () => {
    if (!activeThread) return;
    const label = activeThread.kind === "group" ? "keluar dari grup" : "hapus DM";
    if (!window.confirm(`Yakin ${label} "${activeThread.display_name}"?`)) return;
    try {
      await api.delete(`/karyawan-chat/threads/${activeThread.id}`);
      setActiveId(null);
      loadThreads();
      toast.success("Selesai");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const fmtTime = (iso) => { try { const d = new Date(iso); return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const fmtDay = (iso) => { try { const d = new Date(iso); return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" }); } catch { return ""; } };

  return (
    <div className="max-w-7xl mx-auto p-2 md:p-4 lg:p-6" data-testid="karyawan-chat-page">
      <PageHeader overline="Karyawan · Komunikasi" title="Chat Karyawan" description="Berkomunikasi dengan rekan kerja via DM atau grup." />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-4" style={{ minHeight: "70vh" }}>
        {/* Sidebar — thread list */}
        <Card className={`p-0 md:col-span-4 lg:col-span-3 flex flex-col ${activeId ? "hidden md:flex" : ""}`} data-testid="karyawan-chat-threads">
          <div className="p-3 border-b border-stone-200 flex items-center gap-2">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari thread…" className="flex-1 !text-sm" data-testid="karyawan-chat-search" />
            <Button onClick={() => setNewOpen(true)} className="!px-2 !py-1.5" title="Buat thread baru" data-testid="karyawan-chat-new"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredThreads.length === 0 ? (
              <div className="text-center text-xs text-stone-400 p-8">Belum ada thread. Klik <Plus className="h-3 w-3 inline" /> untuk mulai chat.</div>
            ) : filteredThreads.map((t) => (
              <button key={t.id}
                onClick={() => setActiveId(t.id)}
                className={`w-full text-left p-3 border-b border-stone-100 hover:bg-stone-50 ${activeId === t.id ? "bg-terra-50" : ""}`}
                data-testid={`karyawan-chat-thread-${t.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {t.kind === "group" ? <Users className="h-4 w-4 text-terra-600 shrink-0" /> : <UserCircle className="h-4 w-4 text-stone-600 shrink-0" />}
                    <span className="font-medium text-sm text-stone-900 truncate">{t.display_name}</span>
                  </div>
                  {t.unread_count > 0 && <Badge tone="terra" className="text-[10px]">{t.unread_count}</Badge>}
                </div>
                <p className="text-[11px] text-stone-500 mt-1 truncate">{t.last_message_preview || "(belum ada pesan)"}</p>
                <p className="text-[10px] text-stone-400 mt-0.5">{t.last_message_at ? fmtDay(t.last_message_at) : ""}</p>
              </button>
            ))}
          </div>
        </Card>

        {/* Right — active thread */}
        <Card className={`p-0 md:col-span-8 lg:col-span-9 flex flex-col ${!activeId ? "hidden md:flex" : ""}`} data-testid="karyawan-chat-window">
          {activeThread ? (
            <>
              <div className="p-3 border-b border-stone-200 flex items-center justify-between bg-stone-50/40">
                <div className="flex items-center gap-2 min-w-0">
                  <button onClick={() => setActiveId(null)} className="md:hidden text-stone-600 hover:text-terra-700"><ArrowLeft className="h-4 w-4" /></button>
                  {activeThread.kind === "group" ? <Users className="h-4 w-4 text-terra-600" /> : <UserCircle className="h-4 w-4 text-stone-600" />}
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{activeThread.display_name}</p>
                    <p className="text-[10px] text-stone-500">{activeThread.kind === "group" ? `${activeThread.member_emails.length} anggota` : activeThread.peer_email}</p>
                  </div>
                </div>
                <button onClick={leaveOrDelete} className="text-stone-500 hover:text-red-700 p-1" title={activeThread.kind === "group" ? "Keluar dari grup" : "Hapus DM"} data-testid="karyawan-chat-leave">
                  {activeThread.kind === "group" ? <LogOut className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-stone-50/30" style={{ maxHeight: "60vh" }}>
                {messages.length === 0 && <p className="text-center text-xs text-stone-400 py-8">Belum ada pesan. Mulai ngobrol!</p>}
                {messages.map((m) => {
                  const isMe = (m.sender_email || "").toLowerCase() === me;
                  return (
                    <div key={m.id} className={`flex gap-2 ${isMe ? "justify-end" : "justify-start"}`} data-testid={`karyawan-chat-msg-${m.id}`}>
                      <div className={`max-w-[75%] p-2.5 ${isMe ? "bg-terra-500 text-white" : "bg-white border border-stone-200 text-stone-800"}`}>
                        {!isMe && activeThread.kind === "group" && <p className="text-[10px] font-semibold text-terra-700 mb-0.5">{m.sender_name}</p>}
                        <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                        <p className={`text-[10px] mt-1 text-right ${isMe ? "text-white/70" : "text-stone-400"}`}>{fmtTime(m.ts)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-3 border-t border-stone-200 flex items-center gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())} placeholder="Ketik pesan…" className="flex-1" data-testid="karyawan-chat-input" />
                <Button onClick={send} disabled={sending || !input.trim()} data-testid="karyawan-chat-send"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-stone-400 p-8">
              <MessageSquareText className="h-10 w-10 mb-3" />
              <p className="text-sm">Pilih thread untuk mulai chat</p>
            </div>
          )}
        </Card>
      </div>

      {/* New thread modal */}
      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="Buat Chat Baru" size="md">
        <div className="space-y-3" data-testid="karyawan-chat-new-modal">
          <div className="flex border border-stone-300">
            <button onClick={() => setNewKind("dm")} className={`flex-1 py-2 text-xs uppercase tracking-wider ${newKind === "dm" ? "bg-terra-500 text-white" : "bg-white text-stone-700"}`} data-testid="karyawan-chat-new-dm">DM (1-on-1)</button>
            <button onClick={() => setNewKind("group")} className={`flex-1 py-2 text-xs uppercase tracking-wider ${newKind === "group" ? "bg-terra-500 text-white" : "bg-white text-stone-700"}`} data-testid="karyawan-chat-new-group">Grup</button>
          </div>
          {newKind === "dm" ? (
            <Field label="Pilih karyawan">
              <Select value={peerEmail} onChange={(e) => setPeerEmail(e.target.value)} data-testid="karyawan-chat-new-peer">
                <option value="">— Pilih —</option>
                {directory.map((d) => <option key={d.id} value={d.email}>{d.name} {d.position ? `· ${d.position}` : ""} {d.department_category ? `(${d.department_category})` : ""}</option>)}
              </Select>
            </Field>
          ) : (
            <>
              <Field label="Nama Grup">
                <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Mis: Tim Lapangan Proyek Kemayoran" data-testid="karyawan-chat-new-groupname" />
              </Field>
              <Field label={`Anggota (${groupMembers.length} dipilih)`}>
                <div className="max-h-56 overflow-y-auto border border-stone-200 divide-y divide-stone-100">
                  {directory.map((d) => {
                    const checked = groupMembers.includes(d.email);
                    return (
                      <label key={d.id} className="flex items-center gap-2 p-2 hover:bg-stone-50 cursor-pointer">
                        <input type="checkbox" checked={checked} onChange={(e) => {
                          setGroupMembers((prev) => e.target.checked ? [...prev, d.email] : prev.filter((x) => x !== d.email));
                        }} data-testid={`karyawan-chat-new-member-${d.id}`} />
                        <span className="text-sm">{d.name}</span>
                        {d.department_category && <span className="text-[10px] text-stone-400">· {d.department_category}</span>}
                      </label>
                    );
                  })}
                </div>
              </Field>
            </>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
            <Button variant="outline" onClick={() => setNewOpen(false)}>Batal</Button>
            <Button onClick={createThread} data-testid="karyawan-chat-new-save">Buat</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
